import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";

import SEO from "@/components/SEO";
import { NRF_EVENTS, pushToDataLayer } from "@/services/gtm";
import styles from "@/styles/nrf.module.scss";

export async function getStaticProps({ locale }: any) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "nrf"])),
      // The hero simulator is preview tooling, so it ships only where DEV_MODE
      // asks for it. Read here rather than in the component: the variable is a
      // plain server-side one, and nothing but the resulting boolean is sent to
      // the browser. It is read when the page is rendered — every request under
      // `next dev`, but build time for a deployed build, so a container needs
      // DEV_MODE set for `npm run build`, not only at runtime.
      devMode: process.env.DEV_MODE === "true",
    },
  };
}

/*
 * /nrf — The Invisible Funnel (NRF 2026 Europe).
 *
 * A port of the standalone campaign page, kept deliberately faithful to it:
 * this is a poster, so it carries none of the site chrome — no Navbar, no
 * Footer, its own header and footer, its own type scale. Layout changes here
 * should be mirrored back into the source HTML rather than invented.
 */

/** NRF 2026 Retail's Big Show Europe — Paris Expo Porte de Versailles. */
const SHOW = { year: 2026, month: 9, firstDay: 15, lastDay: 17 } as const;

/** Straight from the standalone page's CONFIG block. */
const CONTACT_EMAIL = "geral@deepneuronic.com";
const WHATSAPP_NUMBER = "351913749899";

type HeroState = "confirm" | "onsite" | "post";

const SRC_TO_STATE: Record<string, HeroState> = {
  linkedin: "confirm",
  email: "confirm",
  google: "confirm",
  bigideas: "confirm",
  meta: "onsite",
  stand: "onsite",
  pitch: "onsite",
  post: "post",
};

type Campaign = {
  src: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
};

const DEFAULT_CAMPAIGN: Campaign = {
  src: "direct",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "nrf2026",
  utm_content: "",
};

// HubSpot form embed (portal 144631172, region eu1). The native form below
// stays as the fallback: ad blockers commonly filter hsforms.net, and a
// three-day campaign page can't afford an empty booking panel.
const HUBSPOT_FORM = {
  portalId: "144631172",
  formId: "8d97621c-adcb-4dc1-bf96-06d5902c27c0",
  region: "eu1",
  scriptSrc: "https://js-eu1.hsforms.net/forms/embed/v2.js",
  scriptId: "hs-forms-embed-v2",
  targetId: "hs-form-nrf",
} as const;

/** The v2 embed's built-in locales: HubSpot ships Portuguese as pt-br. */
const HUBSPOT_LOCALE: Record<string, string> = { en: "en", pt: "pt-br", fr: "fr" };

/** Deliberately permissive, and the same rule /api/nrf-lead applies. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Today's date in Paris, so the hero flips over at the venue's midnight. */
const parisToday = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(new Date());

  const pick = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const year = pick("year");
  const month = pick("month");
  const day = pick("day");

  return {
    year,
    month,
    day,
    // Date.UTC keeps the weekday aligned with the Paris calendar day above.
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
};

const resolveState = (src: string): HeroState => {
  if (SRC_TO_STATE[src]) return SRC_TO_STATE[src];

  const { year, month, day } = parisToday();
  const asNumber = year * 10000 + month * 100 + day;

  if (asNumber < SHOW.year * 10000 + SHOW.month * 100 + SHOW.firstDay) return "confirm";
  if (asNumber > SHOW.year * 10000 + SHOW.month * 100 + SHOW.lastDay) return "post";
  return "onsite";
};

const readCampaign = (): Campaign => {
  const params = new URLSearchParams(window.location.search);
  return {
    src: (params.get("src") || "").toLowerCase() || DEFAULT_CAMPAIGN.src,
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || DEFAULT_CAMPAIGN.utm_campaign,
    utm_content: params.get("utm_content") || "",
  };
};

/** iCalendar escapes `\`, `;`, `,` and newlines in text values (RFC 5545). */
const escapeICS = (value: string) =>
  value.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

export default function NRF({ devMode = false }: { devMode?: boolean }) {
  const { t } = useTranslation("nrf");
  const router = useRouter();

  // `autoState` is what a real visitor gets; `override` is the simulator
  // forcing a different hero. The page renders `override ?? autoState`.
  const [autoState, setAutoState] = useState<HeroState>("confirm");
  const [override, setOverride] = useState<HeroState | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [simOpen, setSimOpen] = useState(false);
  const funnelRef = useRef<HTMLDivElement | null>(null);
  const [funnelInView, setFunnelInView] = useState(false);

  // Native lead form — now the fallback for when the HubSpot embed can't load.
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    preference: "",
    website: "", // honeypot — must stay empty
  });
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<"name" | "email" | null>(null);

  // Mirrors the embed's rule for the fallback form, so the button reads the
  // same way whichever one the visitor ends up with.
  const nativeFormValid = useMemo(
    () => Boolean(form.name.trim()) && EMAIL_PATTERN.test(form.email.trim()),
    [form.email, form.name]
  );

  // The slides pop-up: shown once the session invite has downloaded, so the
  // agenda CTA also captures an address to send the deck to afterwards.
  const [slidesOpen, setSlidesOpen] = useState(false);
  const [slides, setSlides] = useState({ name: "", email: "", website: "" });
  const [slidesStatus, setSlidesStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [slidesError, setSlidesError] = useState<string | null>(null);
  const [slidesInvalid, setSlidesInvalid] = useState<"name" | "email" | null>(null);
  const slidesNameRef = useRef<HTMLInputElement | null>(null);

  const slidesValid = useMemo(
    () => Boolean(slides.name.trim()) && EMAIL_PATTERN.test(slides.email.trim()),
    [slides.email, slides.name]
  );

  // HubSpot embed lifecycle; "failed" switches the native form back on.
  const [hsFailed, setHsFailed] = useState(false);
  // Captured just before HubSpot submits, then mirrored to /api/nrf-lead so
  // the sheet and the Resend confirmation keep working alongside the CRM.
  const hsLeadRef = useRef<{
    name: string;
    email: string;
    company: string;
    preference: string;
  } | null>(null);

  useEffect(() => {
    const detected = readCampaign();
    const heroState = resolveState(detected.src);

    setCampaign(detected);
    setAutoState(heroState);

    pushToDataLayer({
      event: NRF_EVENTS.pageView,
      hero_state: heroState,
      page_locale: router.locale,
      referrer: document.referrer || undefined,
      ...detected,
    });
  }, [router.locale]);

  const state = override ?? autoState;
  const src = campaign?.src ?? DEFAULT_CAMPAIGN.src;

  // The funnel bars hold at zero until they are actually reached.
  useEffect(() => {
    const el = funnelRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setFunnelInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setFunnelInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const trackCta = useCallback(
    (label: string, event: string = NRF_EVENTS.ctaClick) => {
      pushToDataLayer({
        event,
        cta_label: label,
        cta_location: "/nrf",
        hero_state: state,
        ...(campaign ?? DEFAULT_CAMPAIGN),
      });

      // Mirrored into GA4 directly, matching how the site's other CTAs report.
      window.gtag?.("event", "click", {
        event_category: "cta",
        event_label: `NRF · ${label}`,
        value: "1",
      });
    },
    [campaign, state]
  );

  const scrollToBooking = useCallback(
    (label: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      trackCta(label);
      document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
    },
    [trackCta]
  );

  const downloadICS = useCallback(
    (label: string) => {
      trackCta(label, NRF_EVENTS.addToCalendar);

      // 14:45 Paris on Wed 16 Sept is 12:45 UTC (CEST, UTC+2).
      const ics = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//DeepNeuronic//NRF2026//EN",
        "BEGIN:VEVENT",
        "UID:invisible-funnel-nrf2026@deepneuronic.com",
        "DTSTAMP:20260901T090000Z",
        "DTSTART:20260916T124500Z",
        "DTEND:20260916T131500Z",
        `SUMMARY:${escapeICS(t("calendar.summary"))}`,
        `LOCATION:${escapeICS(t("calendar.location"))}`,
        `DESCRIPTION:${escapeICS(t("calendar.description"))}`,
        "END:VEVENT",
        "END:VCALENDAR",
      ].join("\r\n");

      const link = document.createElement("a");
      link.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
      link.download = "invisible-funnel-nrf-europe.ics";
      document.body.appendChild(link);
      link.click();
      link.remove();

      // The invite is already downloaded, so the CTA has done what it says.
      // Only then is the deck offered — and never twice to the same visitor.
      if (slidesStatus !== "sent") {
        setSlidesOpen(true);
        pushToDataLayer({
          event: NRF_EVENTS.slidesPromptOpen,
          hero_state: state,
          ...(campaign ?? DEFAULT_CAMPAIGN),
        });
      }
    },
    [campaign, slidesStatus, state, t, trackCta]
  );

  const closeSlides = useCallback(() => {
    setSlidesOpen(false);
    setSlidesError(null);
    setSlidesInvalid(null);
  }, []);

  // Escape closes it, the page behind it stops scrolling, and the first field
  // takes focus — a dialog that opens without one traps the keyboard.
  useEffect(() => {
    if (!slidesOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeSlides();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    slidesNameRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [closeSlides, slidesOpen]);

  const mailtoHref = useMemo(
    () =>
      `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
        t("email.subject", { src })
      )}&body=${encodeURIComponent(t("email.body"))}`,
    [src, t]
  );

  const whatsappFormHref = useMemo(
    () =>
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("whatsapp.formMessage", { src })
      )}`,
    [src, t]
  );

  const whatsappFloatHref = useMemo(
    () =>
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        t("whatsapp.floatingMessage", { src })
      )}`,
    [src, t]
  );

  const updateField = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (invalidField === field) setInvalidField(null);
  };

  const submitForm = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (formStatus === "sending") return;

      if (!form.name.trim()) {
        setInvalidField("name");
        setFormError(t("book.form.errName"));
        return;
      }
      if (!EMAIL_PATTERN.test(form.email.trim())) {
        setInvalidField("email");
        setFormError(t("book.form.errEmail"));
        return;
      }

      setInvalidField(null);
      setFormError(null);
      setFormStatus("sending");

      try {
        const response = await fetch("/api/nrf-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            ...(campaign ?? DEFAULT_CAMPAIGN),
            heroState: state,
            locale: router.locale,
            pageUrl: window.location.href,
          }),
        });

        if (!response.ok) throw new Error(`lead endpoint returned ${response.status}`);

        setFormStatus("sent");
        trackCta("Lead form", NRF_EVENTS.lead);
        window.gtag?.("event", "generate_lead", {
          event_category: "nrf",
          event_label: "Lead form",
          value: 1,
        });
      } catch (error) {
        console.error("[nrf] lead submit failed:", error);
        setFormStatus("idle");
        setFormError(t("book.form.errFailed"));
      }
    },
    [campaign, form, formStatus, router.locale, state, t, trackCta]
  );

  const updateSlidesField =
    (field: keyof typeof slides) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setSlides((prev) => ({ ...prev, [field]: e.target.value }));
      if (slidesInvalid === field) setSlidesInvalid(null);
    };

  const submitSlides = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (slidesStatus === "sending") return;

      if (!slides.name.trim()) {
        setSlidesInvalid("name");
        setSlidesError(t("slides.errName"));
        return;
      }
      if (!EMAIL_PATTERN.test(slides.email.trim())) {
        setSlidesInvalid("email");
        setSlidesError(t("slides.errEmail"));
        return;
      }

      setSlidesInvalid(null);
      setSlidesError(null);
      setSlidesStatus("sending");

      try {
        const response = await fetch("/api/nrf-lead", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...slides,
            // Lands in the same sheet as a booking, in its own column: these
            // are warm leads, but they have not asked for a demo.
            intent: "slides",
            ...(campaign ?? DEFAULT_CAMPAIGN),
            heroState: state,
            locale: router.locale,
            pageUrl: window.location.href,
          }),
        });

        if (!response.ok) throw new Error(`lead endpoint returned ${response.status}`);

        setSlidesStatus("sent");
        trackCta("Slides request", NRF_EVENTS.slidesLead);
        window.gtag?.("event", "generate_lead", {
          event_category: "nrf",
          event_label: "Slides pop-up",
          value: 1,
        });
      } catch (error) {
        console.error("[nrf] slides submit failed:", error);
        setSlidesStatus("idle");
        setSlidesError(t("slides.errFailed"));
      }
    },
    [campaign, router.locale, slides, slidesStatus, state, t, trackCta]
  );

  // Mount the HubSpot form. Recreated on locale change so placeholders and the
  // submit label follow the page language; falls back to the native form when
  // the script is blocked or the widget misses its deadline.
  useEffect(() => {
    if (hsFailed || formStatus === "sent") return;

    let cancelled = false;
    const deadline = window.setTimeout(() => setHsFailed(true), 8000);

    const create = () => {
      const container = document.getElementById(HUBSPOT_FORM.targetId);
      const hbspt = (window as any).hbspt;
      if (cancelled || !container || !hbspt?.forms) return;
      container.innerHTML = ""; // strict-mode double runs, locale switches

      hbspt.forms.create({
        portalId: HUBSPOT_FORM.portalId,
        formId: HUBSPOT_FORM.formId,
        region: HUBSPOT_FORM.region,
        target: `#${HUBSPOT_FORM.targetId}`,
        locale: HUBSPOT_LOCALE[router.locale || "en"] || "en",
        css: "",
        cssClass: styles.leadForm,
        submitButtonClass: `${styles.btn} ${styles.btnPrimary}`,
        errorClass: styles.fieldError,
        errorMessageClass: styles.formError,
        onFormReady: () => {
          window.clearTimeout(deadline);
          const root = document.getElementById(HUBSPOT_FORM.targetId);
          if (!root) return;

          root.querySelector(".hs_submit .actions")?.classList.add(styles.submitRow);

          // The stylesheet hides HubSpot's labels to match the design, so the
          // copy moves into placeholders — ours where a translation exists,
          // the HubSpot label for any field added to the form later.
          // i18next echoes the key back when a string is missing, which would
          // put "book.form.firstName" in front of a visitor. Treat that as
          // absent and let the HubSpot label take over instead.
          const translate = (key: string) => {
            const value = t(key);
            return value === key ? "" : value;
          };

          const placeholders: Record<string, string> = {
            firstname: translate("book.form.firstName"),
            lastname: translate("book.form.lastName"),
            email: translate("book.form.email"),
            company: translate("book.form.company"),
          };
          root
            .querySelectorAll<HTMLInputElement>(
              "input.hs-input:not([type='hidden']), textarea.hs-input"
            )
            .forEach((input) => {
              const name = input.name.split("/").pop() || input.name;
              const label = root
                .querySelector(`label[for="${input.id}"]`)
                ?.textContent?.trim();
              const text = placeholders[name] || label || "";
              if (text) {
                input.placeholder = text;
                if (!input.getAttribute("aria-label")) input.setAttribute("aria-label", text);
              }
            });

          // submitButtonClass replaces HubSpot's classes outright, so the
          // usual .hs-button hook isn't there — match the element instead.
          // A <select> ignores placeholder: the disabled empty option is what
          // the visitor reads, and HubSpot only ever writes it in English.
          const empty = root.querySelector<HTMLOptionElement>('select option[value=""]');
          if (empty) empty.textContent = translate("book.form.preference") || empty.textContent;

          const button = root.querySelector<HTMLInputElement>('input[type="submit"]');
          if (button) button.value = translate("book.form.submit") || button.value;

          // Hold the submit disabled until the form would actually be accepted.
          // Which fields are required is read from the DOM — HubSpot sets the
          // native `required` attribute — so this survives edits to the form in
          // HubSpot without a code change.
          const formEl = root.querySelector("form");
          if (formEl && button) {
            const refreshSubmit = () => {
              const required = Array.from(
                formEl.querySelectorAll<
                  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >("[required]")
              ).filter((el) => (el as HTMLInputElement).type !== "hidden");

              const filled = required.every((el) =>
                (el as HTMLInputElement).type === "checkbox"
                  ? (el as HTMLInputElement).checked
                  : el.value.trim() !== ""
              );

              // type="email" accepts "a@b"; hold the button to the same rule the
              // API applies, so it can't enable on an address that will bounce
              // off the backend a moment later.
              const email = formEl.querySelector<HTMLInputElement>('input[name="email"]');
              const address = email?.value.trim() || "";
              const emailOk = !email || !address || EMAIL_PATTERN.test(address);

              button.disabled = !(filled && emailOk && formEl.checkValidity());
            };

            formEl.addEventListener("input", refreshSubmit);
            formEl.addEventListener("change", refreshSubmit);
            refreshSubmit();
          }

          root.querySelector("form")?.addEventListener("focusin", () => {
            if ((window as any).deepNeuronicFormStarted) return;
            (window as any).deepNeuronicFormStarted = true;
            pushToDataLayer({ event: "deepneuronic_form_start", form_name: "nrf_demo" });
          });
        },
        onFormSubmit: () => {
          // Read the values before HubSpot clears the form. The `website`
          // field is deliberately NOT captured: /api/nrf-lead uses that name
          // as its honeypot and would silently drop the lead as a bot.
          const root = document.getElementById(HUBSPOT_FORM.targetId);
          const value = (name: string) =>
            root?.querySelector<HTMLInputElement>(`[name="${name}"]`)?.value?.trim() || "";
          hsLeadRef.current = {
            // /api/nrf-lead stores one name and rejects a submission without
            // it, so the two HubSpot fields are recombined here.
            name: [value("firstname"), value("lastname")].filter(Boolean).join(" "),
            email: value("email"),
            company: value("company"),
            preference: value("demo_option"),
          };
        },
        onFormSubmitted: () => {
          setFormStatus("sent");
          pushToDataLayer({ event: "deepneuronic_form_submit", form_name: "nrf_demo" });
          trackCta("Lead form", NRF_EVENTS.lead);
          window.gtag?.("event", "generate_lead", {
            event_category: "nrf",
            event_label: "HubSpot form",
            value: 1,
          });

          // HubSpot owns the contact now; this mirror keeps the sheet filling
          // and the confirmation email going out. Fire-and-forget — the
          // visitor already has their success state either way.
          const lead = hsLeadRef.current;
          if (lead?.email) {
            fetch("/api/nrf-lead", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...lead,
                ...(campaign ?? DEFAULT_CAMPAIGN),
                heroState: state,
                locale: router.locale,
                pageUrl: window.location.href,
              }),
            }).catch((error) => console.error("[nrf] lead mirror failed:", error));
          }
        },
      });
    };

    if ((window as any).hbspt?.forms) {
      create();
    } else {
      let script = document.getElementById(HUBSPOT_FORM.scriptId) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = HUBSPOT_FORM.scriptId;
        script.src = HUBSPOT_FORM.scriptSrc;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", create, { once: true });
      script.addEventListener("error", () => setHsFailed(true), { once: true });
    }

    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
    };
  }, [campaign, formStatus, hsFailed, router.locale, state, t, trackCta]);

  const hero = t(`hero.${state}`, { returnObjects: true }) as {
    kicker: string;
    titleLead: string;
    titlePrefix?: string;
    titleHighlight: string;
    subtitle: string;
    chips: string[];
    todayChip?: string;
    withLabel?: string;
    primaryCta: string;
    secondaryCta?: string;
  };

  const weekdays = t("weekdays", { returnObjects: true }) as string[];
  const cards = t("cards", { returnObjects: true }) as {
    label: string;
    headline: string;
    detail: string;
  }[];
  const funnelStages = t("funnel.stages", { returnObjects: true }) as string[];

  // Only meaningful while the show is running, and only known after mount.
  const todayChip = useMemo(() => {
    if (state !== "onsite" || !hero.todayChip) return null;
    const { weekday, day } = parisToday();
    return t("hero.onsite.todayChip", { weekday: weekdays[weekday], day });
  }, [hero.todayChip, state, t, weekdays]);

  const chips = [...hero.chips, ...(todayChip ? [todayChip] : [])];

  return (
    <>
      <SEO
        title={t("meta.title")}
        description={t("meta.description")}
        ogImage="/nrf/og.png"
        ogImageWidth={2400}
        ogImageHeight={1260}
      />

      <div className={styles.page}>
        <div className={styles.glow} />

        <div className={styles.wrap}>
          <header>
            <div className={styles.brand}>
              <Link href="/" aria-label="DeepNeuronic" onClick={() => trackCta("Logo home")}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img alt="DeepNeuronic" src="/nrf/logo.png" />
              </Link>
              <span className={styles.kicker}>{t("brandKicker")}</span>
            </div>
            <div className={styles.nrf}>
              <b>{t("badge.show")}</b>
              <span>{t("badge.tagline")}</span>
            </div>
          </header>

          {/* ===================== HERO ===================== */}
          <section className={styles.hero}>
            <div className={styles.kicker}>{hero.kicker}</div>

            <h1>
              {hero.titleLead}
              <br />
              {hero.titlePrefix ? `${hero.titlePrefix} ` : ""}
              <span className={styles.hl}>{hero.titleHighlight}</span>
            </h1>

            <p className={styles.sub}>{hero.subtitle}</p>

            <div className={styles.metaLine}>
              {chips.map((chip) => (
                <span key={chip} className={styles.chip}>
                  {chip}
                </span>
              ))}
            </div>

            {hero.withLabel && (
              <div className={styles.speaker}>
                <i />
                <span>{hero.withLabel}&nbsp;</span>
                <b>{t("partner")}</b>
              </div>
            )}

            <div className={styles.ctas}>
              <a
                className={`${styles.btn} ${styles.btnPrimary}`}
                href="#book"
                onClick={scrollToBooking(hero.primaryCta)}
              >
                {hero.primaryCta}
              </a>
              {hero.secondaryCta && (
                <button
                  className={`${styles.btn} ${styles.btnGhost}`}
                  onClick={() => downloadICS(hero.secondaryCta as string)}
                >
                  {hero.secondaryCta}
                </button>
              )}
            </div>
          </section>

          {/* ===================== INFO CARDS ===================== */}
          <div className={styles.cards}>
            {cards.map((card) => (
              <div className={styles.card} key={card.label}>
                <h3>{card.label}</h3>
                <p>
                  <b>{card.headline}</b>
                </p>
                <small>{card.detail}</small>
              </div>
            ))}
          </div>

          {/* ===================== THE FUNNEL ===================== */}
          <section className={styles.block}>
            <div className={styles.kicker}>{t("funnel.kicker")}</div>
            <h2>
              {t("funnel.titleLead")}{" "}
              <span className={styles.hl}>{t("funnel.titleHighlight")}</span>
            </h2>
            <p className={styles.lead}>{t("funnel.lead")}</p>

            <div
              ref={funnelRef}
              className={`${styles.funnel} ${funnelInView ? styles.inView : ""}`}
            >
              {funnelStages.map((stage) => (
                <div className={styles.bar} key={stage}>
                  <i />
                  <span>{stage}</span>
                </div>
              ))}
            </div>

            <p className={styles.lead}>{t("funnel.closing")}</p>
            <p className={styles.privacyLine}>
              {t("funnel.privacy")} <b>{t("funnel.privacyHighlight")}</b>
            </p>
          </section>

          {/* ===================== FORM ===================== */}
          <section className={`${styles.block} ${styles.book}`} id="book">
            <div className={styles.kicker}>{t("book.kicker")}</div>
            <h2>
              {t("book.titleLead")}{" "}
              <span className={styles.hl}>{t("book.titleHighlight")}</span>
            </h2>
            <p className={styles.lead}>{t("book.lead")}</p>

            <div className={styles.formPanel} style={{ marginTop: 26 }}>
              {formStatus === "sent" ? (
                <div className={styles.formSuccess}>
                  <strong>{t("book.form.successTitle")}</strong>
                  <span>{t("book.form.successBody")}</span>
                </div>
              ) : hsFailed ? (
                <form className={styles.leadForm} onSubmit={submitForm} noValidate>
                  <input
                    name="name"
                    autoComplete="name"
                    placeholder={t("book.form.name")}
                    value={form.name}
                    onChange={updateField("name")}
                    disabled={formStatus === "sending"}
                    className={invalidField === "name" ? styles.fieldError : undefined}
                  />
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={t("book.form.email")}
                    value={form.email}
                    onChange={updateField("email")}
                    disabled={formStatus === "sending"}
                    className={invalidField === "email" ? styles.fieldError : undefined}
                  />
                  <input
                    name="company"
                    autoComplete="organization"
                    placeholder={t("book.form.company")}
                    value={form.company}
                    onChange={updateField("company")}
                    disabled={formStatus === "sending"}
                  />
                  <input
                    name="preference"
                    placeholder={t("book.form.preference")}
                    value={form.preference}
                    onChange={updateField("preference")}
                    disabled={formStatus === "sending"}
                  />

                  {/* Honeypot: invisible to people, irresistible to bots. */}
                  <div className={styles.honeypot} aria-hidden="true">
                    <input
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={updateField("website")}
                    />
                  </div>

                  {formError && <p className={styles.formError}>{formError}</p>}

                  <div className={styles.submitRow}>
                    <button
                      type="submit"
                      className={`${styles.btn} ${styles.btnPrimary}`}
                      disabled={formStatus === "sending" || !nativeFormValid}
                    >
                      {formStatus === "sending"
                        ? t("book.form.submitting")
                        : t("book.form.submit")}
                    </button>
                  </div>

                </form>
              ) : (
                <div id={HUBSPOT_FORM.targetId} className={styles.hsForm} />
              )}

              {formStatus !== "sent" && (
                <>
                  <p className={styles.altLine}>{t("book.form.alternative")}</p>
                  <div className={styles.formFallback}>
                    <a
                      className={`${styles.btn} ${styles.btnGhost}`}
                      href={mailtoHref}
                      onClick={() => trackCta("Email us")}
                    >
                      {t("book.emailCta")}
                    </a>
                    <a
                      className={`${styles.btn} ${styles.btnGhost}`}
                      href={whatsappFormHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackCta("WhatsApp us")}
                    >
                      {t("book.whatsappCta")}
                    </a>
                  </div>
                </>
              )}

              <p className={styles.hsNote}>{t("book.note")}</p>
            </div>
          </section>
        </div>

        <footer>
          <div className={`${styles.wrap} ${styles.footFlex}`}>
            <div>
              {t("footer.tagline")} <b>{t("footer.taglineHighlight")}</b>
            </div>
            <div>{t("footer.legal")}</div>
          </div>
        </footer>

        {/* The mark rather than the word: the glyph is recognised on sight and
            keeps the button to a circle. The label lives on aria-label/title,
            so it still reads as "WhatsApp" to a screen reader and on hover. */}
        <a
          className={styles.wa}
          href={whatsappFloatHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("whatsapp.floating")}
          title={t("whatsapp.floating")}
          onClick={() => trackCta("WhatsApp floating")}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
          </svg>
        </a>

        {/* ============ SLIDES POP-UP (after the calendar download) ============ */}
        {slidesOpen && (
          <div
            className={styles.modalOverlay}
            onClick={closeSlides}
            role="presentation"
          >
            <div
              className={styles.modal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="nrf-slides-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.modalClose}
                onClick={closeSlides}
                aria-label={t("slides.close")}
              >
                &times;
              </button>

              {slidesStatus === "sent" ? (
                <>
                  <div className={styles.formSuccess}>
                    <strong>{t("slides.successTitle")}</strong>
                    <span>{t("slides.successBody")}</span>
                  </div>
                  <div className={styles.submitRow}>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnGhost}`}
                      onClick={closeSlides}
                    >
                      {t("slides.close")}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.kicker}>{t("slides.kicker")}</div>
                  <h3 id="nrf-slides-title" className={styles.modalTitle}>
                    {t("slides.titleLead")}{" "}
                    <span className={styles.hl}>{t("slides.titleHighlight")}</span>
                  </h3>
                  <p className={styles.modalBody}>{t("slides.body")}</p>

                  <form className={styles.leadForm} onSubmit={submitSlides} noValidate>
                    <input
                      ref={slidesNameRef}
                      name="name"
                      autoComplete="name"
                      placeholder={t("slides.name")}
                      value={slides.name}
                      onChange={updateSlidesField("name")}
                      disabled={slidesStatus === "sending"}
                      className={slidesInvalid === "name" ? styles.fieldError : undefined}
                    />
                    <input
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder={t("slides.email")}
                      value={slides.email}
                      onChange={updateSlidesField("email")}
                      disabled={slidesStatus === "sending"}
                      className={slidesInvalid === "email" ? styles.fieldError : undefined}
                    />

                    {/* Honeypot: invisible to people, irresistible to bots. */}
                    <div className={styles.honeypot} aria-hidden="true">
                      <input
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={slides.website}
                        onChange={updateSlidesField("website")}
                      />
                    </div>

                    {slidesError && <p className={styles.formError}>{slidesError}</p>}

                    <div className={`${styles.submitRow} ${styles.modalActions}`}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnPrimary}`}
                        disabled={slidesStatus === "sending" || !slidesValid}
                      >
                        {slidesStatus === "sending"
                          ? t("slides.submitting")
                          : t("slides.submit")}
                      </button>
                    </div>
                  </form>

                  <p className={styles.hsNote}>{t("slides.note")}</p>
                </>
              )}
            </div>
          </div>
        )}

        {devMode && (
          <div className={styles.sim}>
            {simOpen && (
              <div className={styles.simPanel}>
                <h4>{t("sim.title")}</h4>
                {(
                  [
                    [null, t("sim.auto"), t("sim.autoDesc")],
                    ["confirm", t("sim.before"), t("sim.beforeDesc")],
                    ["onsite", t("sim.during"), t("sim.duringDesc")],
                    ["post", t("sim.after"), t("sim.afterDesc")],
                  ] as [HeroState | null, string, string][]
                ).map(([value, label, description]) => (
                  <button
                    key={label}
                    className={`${styles.simOption} ${
                      override === value ? styles.simOptionActive : ""
                    }`}
                    onClick={() => setOverride(value)}
                  >
                    <strong>{label}</strong>
                    <small>{description}</small>
                  </button>
                ))}
              </div>
            )}

            <button
              className={styles.simToggle}
              onClick={() => setSimOpen((open) => !open)}
              aria-expanded={simOpen}
            >
              {t("sim.toggle")}
              <em>
                {override === null
                  ? t("sim.auto")
                  : override === "confirm"
                  ? t("sim.before")
                  : override === "onsite"
                  ? t("sim.during")
                  : t("sim.after")}
              </em>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
